import React, { type ComponentType } from 'react';
import '@atlaskit/css-reset';
import { Box, xcss } from '@atlaskit/primitives';
import AppBase from '@atlassian/jira-app-base';
import { ProjectContextProvider } from '@atlassian/jira-business-entity-project-provider';
import ListView from '@atlassian/jira-business-list';
import { ListFeaturesProvider } from '@atlassian/jira-business-list/src/controllers/features-context';
import { SeenIt } from '@atlassian/jira-business-onboarding-common';
import {
	ViewPreferencesProvider,
	LIST_VIEW_PREFERENCES,
} from '@atlassian/jira-business-preferences';
import { PAGE_LAYOUT_OFFSET_TOP } from '@atlassian/jira-common-constants/src/page-layout';
import { ff } from '@atlassian/jira-feature-flagging';
import { useBoard } from '@atlassian/jira-navigation-apps-sidebar-common';
import { useFeatures } from '@atlassian/jira-navigation-apps-sidebar-next-gen-use-features';
import { useIsSubtasksEnabled } from '@atlassian/jira-router-resources-business-configuration';
import { useProject } from '@atlassian/jira-router-resources-business-project-details';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { PageHeader } from './ui';

const LIST_NAV_ITEM_SEEN_IT_FLAG = 'jsw.list.nav-item-flag';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type JiraSoftwareListAppType = Record<any, any>;
const JiraSoftwareListApp: ComponentType<JiraSoftwareListAppType> = () => {
	const { data: features } = useFeatures();
	const { data: board } = useBoard();
	const isScrumProject = board?.type === 'scrum' || features?.hasSprint;
	const { data: project } = useProject();
	const isSubtasksEnabled = useIsSubtasksEnabled();
	const isSimplifiedProject = Boolean(project?.isSimplified);
	const canCreateIssues = Boolean(project?.permissions.createIssues);
	const canCreateSubtask = canCreateIssues && (isSimplifiedProject || isSubtasksEnabled);

	return (
		<UFOSegment name="software-list-app">
			<AuthenticationCheck>
				<AppBase id="software-list">
					<ProjectContextProvider>
						<ListFeaturesProvider
							featureView="software-list"
							hasCreateSubtask={canCreateSubtask}
							canCreateIssues={canCreateIssues}
							isSoftwareFieldsEnabled
							isBusinessFieldsEnabled={false}
							isScrumProject={isScrumProject}
						>
							<ViewPreferencesProvider view="list" preferences={LIST_VIEW_PREFERENCES}>
								<Box
									xcss={[
										pageContainerStyles,
										ff('jsw.list.sidebar-layout-update') && fullHeightPageContainerStyles,
									]}
								>
									{!ff('jsw.list.sidebar-layout-update') && <PageHeader />}
									<Box
										xcss={
											ff('jsw.list.sidebar-layout-update')
												? listContainerStyles
												: listContainerStylesOld
										}
									>
										<ListView header={ff('jsw.list.sidebar-layout-update') && <PageHeader />} />
									</Box>
								</Box>
							</ViewPreferencesProvider>
						</ListFeaturesProvider>
					</ProjectContextProvider>
					<SubProductUpdater subProduct="software" />
					<SeenIt flag={LIST_NAV_ITEM_SEEN_IT_FLAG} />
				</AppBase>
			</AuthenticationCheck>
		</UFOSegment>
	);
};

const PAGE_HEADER_HEIGHT = 75;
const LIST_PADDING = 24;

const pageContainerStyles = xcss({
	paddingLeft: 'space.200',
});

const fullHeightPageContainerStyles = xcss({
	height: '100%',
});

const listContainerStyles = xcss({
	height: '100%',
});

const listContainerStylesOld = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP} - ${PAGE_HEADER_HEIGHT}px - ${LIST_PADDING}px)`,
});

export default JiraSoftwareListApp;
